import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { toggleTestMode } from '../../../services/testAccountService';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';


const TestModeToggleModal = ({ user, isOpen, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const newValue = !user?.is_test_account;
      const result = await toggleTestMode(user?.id, newValue, reason || null);
      
      if (result?.success) {
        toast?.success(`Test mode ${newValue ? 'enabled' : 'disabled'} for ${user?.full_name || user?.email}`);
        onSuccess?.();
        onClose();
      } else {
        toast?.error(result?.error || 'Failed to toggle test mode');
      }
    } catch (error) {
      toast?.error('Failed to toggle test mode');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
        <h3 className="text-xl font-semibold text-foreground mb-4">
          {user?.is_test_account ? 'Disable' : 'Enable'} Test Mode
        </h3>
        
        <div className="space-y-4 mb-6">
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <p className="text-sm text-foreground mb-2">
              <strong>User:</strong> {user?.full_name || 'N/A'} ({user?.email})
            </p>
            <p className="text-sm text-foreground mb-2">
              <strong>Current Status:</strong>{' '}
              <span className={user?.is_test_account ? 'text-warning font-medium' : 'text-muted-foreground'}>
                {user?.is_test_account ? 'Test Account' : 'Regular Account'}
              </span>
            </p>
            <p className="text-sm text-foreground">
              <strong>New Status:</strong>{' '}
              <span className={!user?.is_test_account ? 'text-warning font-medium' : 'text-muted-foreground'}>
                {!user?.is_test_account ? 'Test Account' : 'Regular Account'}
              </span>
            </p>
          </div>

          {!user?.is_test_account && (
            <div className="bg-info/10 border border-info/20 rounded-lg p-4">
              <p className="text-sm text-foreground font-medium mb-2">Test Mode Benefits:</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Unlimited credits (no deduction)</li>
                <li>No Stripe payment requirements</li>
                <li>No card-on-file requirement</li>
                <li>Mock expert payouts</li>
                <li>Full feature access</li>
              </ul>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reason (Optional)
            </label>
            <Input
              type="text"
              placeholder="e.g., Internal testing, Demo account, QA purposes"
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              className="w-full"
            />
          </div>

          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="text-sm text-error font-medium mb-1">⚠️ Security Notice</p>
            <p className="text-xs text-muted-foreground">
              This action will be logged in the audit trail and may trigger email notifications to super admins.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant={user?.is_test_account ? 'default' : 'primary'}
            onClick={handleToggle}
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? 'Processing...' : user?.is_test_account ? 'Disable Test Mode' : 'Enable Test Mode'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const UserManagementTable = () => {
  const [searchParams] = useSearchParams();
  const highlightUserId = searchParams.get('userId');
  const highlightRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [testAccountFilter, setTestAccountFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, is_test_account, created_at, updated_at')
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setUsers(data || []);
    } catch (error) {
      console.error('Fetch users error:', error);
      toast?.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-scroll to highlighted user after data loads
  useEffect(() => {
    if (!isLoading && highlightUserId && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [isLoading, highlightUserId]);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'client', label: 'Client' },
    { value: 'expert', label: 'Expert' },
    { value: 'admin', label: 'Admin' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' },
  ];

  const testAccountOptions = [
    { value: 'all', label: 'All Accounts' },
    { value: 'test', label: 'Test Accounts Only' },
    { value: 'regular', label: 'Regular Accounts Only' },
  ];

  const filteredUsers = users?.filter((user) => {
    const matchesSearch =
      user?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesTestAccount = 
      testAccountFilter === 'all' ||
      (testAccountFilter === 'test' && user?.is_test_account) ||
      (testAccountFilter === 'regular' && !user?.is_test_account);
    return matchesSearch && matchesRole && matchesTestAccount;
  });

  const handleToggleTestMode = (user) => {
    setSelectedUser(user);
    setIsTestModalOpen(true);
  };

  const handleViewDetails = (userId) => {
    console.log('View user details:', userId);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Input
          type="search"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="w-full"
        />
        <Select
          options={roleOptions}
          value={roleFilter}
          onChange={setRoleFilter}
          placeholder="Filter by role"
        />
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Filter by status"
        />
        <Select
          options={testAccountOptions}
          value={testAccountFilter}
          onChange={setTestAccountFilter}
          placeholder="Filter by account type"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading users...</div>
      ) : (
        <>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-foreground">User</th>
                  <th className="text-left p-4 text-sm font-medium text-foreground">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-foreground">Account Type</th>
                  <th className="text-left p-4 text-sm font-medium text-foreground">Joined</th>
                  <th className="text-right p-4 text-sm font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers?.map((user) => {
                  const isHighlightedRow = highlightUserId && user?.id === highlightUserId;
                  return (
                  <tr
                    key={user?.id}
                    ref={isHighlightedRow ? highlightRef : null}
                    className={`border-b border-border hover:bg-muted/50 ${isHighlightedRow ? 'bg-warning/10 ring-2 ring-inset ring-warning/40' : ''}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium text-foreground">{user?.full_name || 'N/A'}</p>
                          <p className="text-sm text-muted-foreground">{user?.email}</p>
                        </div>
                        {user?.is_test_account && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                            TEST
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          user?.is_test_account
                            ? 'bg-warning/10 text-warning border border-warning/20'
                            : user?.role === 'admin' ?'bg-error/10 text-error border border-error/20'
                            : user?.role === 'expert' ?'bg-secondary/10 text-secondary border border-secondary/20' :'bg-primary/10 text-primary border border-primary/20'
                        }`}
                      >
                        {user?.is_test_account ? (
                          <>
                            <Icon name="TestTube" size={12} className="mr-1" />
                            Test Account
                          </>
                        ) : (
                          user?.role?.toUpperCase()
                        )}
                      </span>
                      {user?.test_account_status === 'pending' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                          <Icon name="Clock" size={12} className="mr-1" />
                          Test Pending
                        </span>
                      )}
                      {user?.test_account_status === 'rejected' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error border border-error/20">
                          <Icon name="XCircle" size={12} className="mr-1" />
                          Test Rejected
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user?.is_test_account
                          ? 'bg-warning/10 text-warning' :'bg-success/10 text-success'
                      }`}>
                        {user?.is_test_account ? 'Test Account' : 'Regular'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {new Date(user?.created_at)?.toLocaleDateString()}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          onClick={() => handleViewDetails(user?.id)}
                        >
                          View
                        </Button>
                        <Button
                          variant={user?.is_test_account ? 'outline' : 'default'}
                          size="sm"
                          iconName={user?.is_test_account ? 'XCircle' : 'TestTube2'}
                          onClick={() => handleToggleTestMode(user)}
                        >
                          {user?.is_test_account ? 'Disable Test' : 'Enable Test'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden space-y-4">
            {filteredUsers?.map((user) => {
              const isHighlightedCard = highlightUserId && user?.id === highlightUserId;
              return (
              <div
                key={user?.id}
                ref={isHighlightedCard ? highlightRef : null}
                className={`bg-card border rounded-lg p-4 ${isHighlightedCard ? 'border-warning ring-2 ring-warning/40' : 'border-border'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-foreground">{user?.full_name || 'N/A'}</h4>
                      {user?.is_test_account && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                          TEST
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{user?.email}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{user?.role}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        user?.is_test_account
                          ? 'bg-warning/10 text-warning' :'bg-success/10 text-success'
                      }`}>
                        {user?.is_test_account ? 'Test Account' : 'Regular'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Eye"
                    onClick={() => handleViewDetails(user?.id)}
                  >
                    View
                  </Button>
                  <Button
                    variant={user?.is_test_account ? 'outline' : 'default'}
                    size="sm"
                    fullWidth
                    iconName={user?.is_test_account ? 'XCircle' : 'TestTube2'}
                    onClick={() => handleToggleTestMode(user)}
                  >
                    {user?.is_test_account ? 'Disable Test' : 'Enable Test'}
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      <TestModeToggleModal
        user={selectedUser}
        isOpen={isTestModalOpen}
        onClose={() => {
          setIsTestModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={fetchUsers}
      />
    </div>
  );
};

export default UserManagementTable;